import storeConfigs from '@/constants/storeConfigs'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref } from 'vue'

type UserTourState = {
	completed: boolean
	skipped: boolean
}

export type TourStepContext = {
	target: HTMLElement | null
	isFirst: boolean
	isLast: boolean
	next: () => void
	back: () => void
	cancel: () => void
	complete: () => void
}

const NULL_CONTEXT: TourStepContext = {
	target: null,
	isFirst: false,
	isLast: false,
	next: () => {},
	back: () => {},
	cancel: () => {},
	complete: () => {},
}

export const useTourStore = defineStore(
	storeConfigs.tour.name,
	() => {
		const seenByUser = ref<Record<string, UserTourState>>({})
		const active = ref(false)
		const stepContext = ref<TourStepContext>({ ...NULL_CONTEXT })

		function stateFor(userId: number | string | undefined): UserTourState {
			if (userId === undefined || userId === null) {
				return { completed: false, skipped: false }
			}
			return seenByUser.value[String(userId)] ?? { completed: false, skipped: false }
		}

		function shouldAutoStart(userId: number | string | undefined) {
			if (userId === undefined || userId === null) return false
			const state = stateFor(userId)
			return !state.completed && !state.skipped
		}

		function markCompleted(userId: number | string | undefined) {
			if (userId === undefined || userId === null) return
			seenByUser.value = {
				...seenByUser.value,
				[String(userId)]: { completed: true, skipped: false },
			}
		}

		function markSkipped(userId: number | string | undefined) {
			if (userId === undefined || userId === null) return
			seenByUser.value = {
				...seenByUser.value,
				[String(userId)]: { completed: false, skipped: true },
			}
		}

		function reset(userId: number | string | undefined) {
			if (userId === undefined || userId === null) return
			const next = { ...seenByUser.value }
			delete next[String(userId)]
			seenByUser.value = next
		}

		function setActive(value: boolean) {
			active.value = value
		}

		function setStepContext(ctx: TourStepContext | null) {
			stepContext.value = ctx ?? { ...NULL_CONTEXT }
		}

		function $reset() {
			active.value = false
			stepContext.value = { ...NULL_CONTEXT }
		}

		return {
			seenByUser,
			active,
			stepContext,
			stateFor,
			shouldAutoStart,
			markCompleted,
			markSkipped,
			reset,
			setActive,
			setStepContext,
			$reset,
		}
	},
	{
		persist: storeConfigs.tour.persist,
	}
)

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useTourStore, import.meta.hot))
}
